import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    ListItem,
    ListItemText,
    Typography,
} from '@material-ui/core';

import { Block } from '@klp/design';
import { ActionButton } from '@klp/design/ActionButton';
import { Illustration, IconName } from '@klp/design/Illustrations';

import { useGrantAdminMutation, useRevokeAdminMutation } from '../rtkApi';

interface IToggleAdminProps {
    isAdmin: boolean;
    email: string;
    onSuccess: () => void;
}

export const ToggleAdmin: React.FC<IToggleAdminProps> = ({
    isAdmin,
    email,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [displayDialog, setDisplayDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [grantAdmin] = useGrantAdminMutation();
    const [revokeAdmin] = useRevokeAdminMutation();

    const handleToggleAdminOpen = () => {
        setDisplayDialog(true);
    };

    const handleToggleAdminClose = () => {
        if (!isLoading) {
            setDisplayDialog(false);
        }
    };

    const handleToggleAdminConfirmed = async () => {
        setIsLoading(true);
        try {
            if (isAdmin) {
                await revokeAdmin(email).unwrap();
            } else {
                await grantAdmin(email).unwrap();
            }
            onSuccess();
            setDisplayDialog(false);
        } catch (error) {
            // Error handling is done by RTK Query
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Block>
                <ListItem
                    dense={true}
                    button={true}
                    onClick={handleToggleAdminOpen}
                >
                    <Block paddingRight="compact">
                        <Illustration icon={IconName.PROFILE} size="medium" />
                    </Block>
                    <ListItemText>
                        {isAdmin ? (
                            <Typography>
                                {t('userSearch.revokeAdminUser')}
                            </Typography>
                        ) : (
                            <Typography>
                                {t('userSearch.grantAdminUser')}
                            </Typography>
                        )}
                    </ListItemText>
                </ListItem>
            </Block>
            <Dialog open={displayDialog} onClose={handleToggleAdminClose}>
                <DialogContent>
                    {isAdmin ? (
                        <DialogContentText>
                            {t('userSearch.revokeAdminQuestion', { email })}
                        </DialogContentText>
                    ) : (
                        <>
                            <DialogContentText>
                                {t('userSearch.grantAdminQuestion', { email })}
                            </DialogContentText>
                            <Typography variant="caption" color="textSecondary">
                                {t('listUsers.adminUserExplanation')}
                            </Typography>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <ActionButton
                        actionType="cancel"
                        translation="general.decline"
                        onClick={handleToggleAdminClose}
                        disabled={isLoading}
                    />
                    <ActionButton
                        actionType="forward"
                        translation="general.confirm"
                        onClick={handleToggleAdminConfirmed}
                        isLoading={isLoading}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
